import { useMemo, useState } from "react";

import { Link } from "react-router-dom";

import dayjs from "dayjs";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Download,
  Eye,
  FileText,
  Loader2,
  MoreHorizontal,
  Paperclip,
  Plus,
  Search,
  Trash2,
  Upload,
} from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { EmptyState } from "~/components/ui/empty-state";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { TablePagination } from "~/components/ui/table-pagination";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { useCompanies } from "~/hooks/contabilidad/useCompanies";
import {
  useRemoveSupplierInvoicePdf,
  useSetSupplierInvoiceCompany,
  useSupplierInvoices,
} from "~/hooks/purchasing";
import { usePagination } from "~/hooks/usePagination";
import { formatCurrency, formatDate } from "~/utils/helpers";


import { AdjuntarPdfDialog } from "./components/AdjuntarPdfDialog";
import { ContabilizarFacturaDialog } from "./components/ContabilizarFacturaDialog";
import { SupplierInvoiceImportDialog } from "./components/SupplierInvoiceImportDialog";
import { SupplierInvoiceManualDialog } from "./components/SupplierInvoiceManualDialog";
import {
  exportSupplierInvoicesToExcel,
  TIPO_LABEL,
} from "./utils/exportSupplierInvoices";

import type { SupplierInvoice } from "~/types/purchasing";

const POSTING_CHIP: Record<string, { label: string; className: string }> = {
  sin_contabilizar: {
    label: "Sin contabilizar",
    className: "bg-gray-100 text-gray-600",
  },
  borrador: { label: "Borrador", className: "bg-amber-100 text-amber-800" },
  contabilizada: {
    label: "Contabilizada",
    className: "bg-green-100 text-green-800",
  },
};

type SortKey =
  | "proveedor"
  | "tipo"
  | "folio"
  | "emision"
  | "vencimiento"
  | "estado"
  | "total"
  | "contabilizacion";
type SortDir = "asc" | "desc";

// Sort value per column: strings compare locale-aware (es), numbers/dates numerically.
function invSortValue(inv: SupplierInvoice, key: SortKey): string | number {
  switch (key) {
    case "proveedor":
      return (inv.supplier?.name ?? inv.razon_social ?? "").toLowerCase();
    case "tipo":
      return TIPO_LABEL[inv.doc_tipo_code] ?? inv.doc_tipo_code ?? "";
    case "folio": {
      const n = Number(inv.folio);
      return Number.isFinite(n) ? n : String(inv.folio ?? "");
    }
    case "emision":
      return inv.emission_date ? dayjs(inv.emission_date).valueOf() : 0;
    case "vencimiento":
      return inv.due_date ? dayjs(inv.due_date).valueOf() : 0;
    case "estado":
      return inv.bsale_estado ?? "";
    case "total":
      return Number(inv.total_clp);
    case "contabilizacion":
      return inv.posting_status ?? "";
  }
}

function SortableHeader({
  label,
  sortKey,
  sort,
  onSort,
  align = "left",
  className,
}: {
  label: string;
  sortKey: SortKey;
  sort: { key: SortKey; dir: SortDir };
  onSort: (key: SortKey) => void;
  align?: "left" | "right";
  className?: string;
}) {
  const active = sort.key === sortKey;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        aria-label={`Ordenar por ${label}`}
        className={`flex w-full items-center gap-1 transition-colors hover:text-foreground ${
          align === "right" ? "justify-end" : ""
        }`}
      >
        <span>{label}</span>
        <Icon
          className={`h-3.5 w-3.5 shrink-0 ${
            active ? "text-foreground" : "text-muted-foreground/50"
          }`}
        />
      </button>
    </TableHead>
  );
}

export function SupplierInvoicesPage() {
  const [search, setSearch] = useState("");
  const [tipo, setTipo] = useState<string>("all");
  const [pdfStatus, setPdfStatus] = useState<string>("all");
  const [contab, setContab] = useState<string>("all");
  const [company, setCompany] = useState<string>("all");
  // Default to the current month — this page is only for reviewing + contabilizar the
  // month's received DTEs. Payments live in Contabilidad → Pagos a Proveedor.
  const [dateFrom, setDateFrom] = useState(() =>
    dayjs().startOf("month").format("YYYY-MM-DD")
  );
  const [dateTo, setDateTo] = useState(() => dayjs().format("YYYY-MM-DD"));
  const [importOpen, setImportOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [postingInvoice, setPostingInvoice] = useState<SupplierInvoice | null>(
    null
  );
  const [attachInvoice, setAttachInvoice] = useState<SupplierInvoice | null>(
    null
  );
  const [removeInvoice, setRemoveInvoice] = useState<SupplierInvoice | null>(
    null
  );
  const removeManualPdf = useRemoveSupplierInvoicePdf();
  // Sortable columns: default alphabetically (A→Z) by proveedor.
  const [sortConfig, setSortConfig] = useState<{ key: SortKey; dir: SortDir }>({
    key: "proveedor",
    dir: "asc",
  });
  const handleSort = (key: SortKey) =>
    setSortConfig((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );

  const { data: companies = [] } = useCompanies();
  const setInvoiceCompany = useSetSupplierInvoiceCompany();

  const { data: invoices = [], isLoading } = useSupplierInvoices({
    search: search || undefined,
    doc_tipo_code: tipo === "all" ? undefined : tipo,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    pdfStatus:
      pdfStatus === "all" ? undefined : (pdfStatus as "with" | "without"),
    company_id: company === "all" ? undefined : company,
  });

  // posting_status is derived at read → filter the loaded list client-side.
  const filtered = useMemo(
    () =>
      contab === "all"
        ? invoices
        : invoices.filter((i) => i.posting_status === contab),
    [invoices, contab]
  );

  const total = useMemo(
    () => filtered.reduce((s, i) => s + Number(i.total_clp), 0),
    [filtered]
  );

  // Apply the active column sort before paginating.
  const sorted = useMemo(() => {
    const { key, dir } = sortConfig;
    return [...filtered].sort((a, b) => {
      const va = invSortValue(a, key);
      const vb = invSortValue(b, key);
      const cmp =
        typeof va === "number" && typeof vb === "number"
          ? va - vb
          : String(va).localeCompare(String(vb), "es", {
              sensitivity: "base",
              numeric: true,
            });
      return dir === "asc" ? cmp : -cmp;
    });
  }, [filtered, sortConfig]);

  const pagination = usePagination(sorted, 20);

  return (
    <PageContainer>
      <PageHeader
        title="Facturas de Proveedor"
        description="Documentos y DTE de compra recibidos (BSale / SII) — revisar y contabilizar"
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => exportSupplierInvoicesToExcel(filtered)}
              disabled={filtered.length === 0}
            >
              <Download className="mr-2 h-4 w-4" />
              Exportar
            </Button>
            <Button variant="outline" onClick={() => setManualOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Cargar manual
            </Button>
            <Button onClick={() => setImportOpen(true)}>
              <Upload className="mr-2 h-4 w-4" />
              Cargar facturas (BSale)
            </Button>
          </>
        }
      />

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              {filtered.length} facturas · ${formatCurrency(total)}
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Input
                type="date"
                aria-label="Desde"
                value={dateFrom}
                max={dateTo}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-9 w-full sm:w-40"
              />
              <Input
                type="date"
                aria-label="Hasta"
                value={dateTo}
                min={dateFrom}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-9 w-full sm:w-40"
              />
              <Select value={company} onValueChange={setCompany}>
                <SelectTrigger className="h-9 w-full sm:w-48">
                  <SelectValue placeholder="Empresa" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las empresas</SelectItem>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={tipo} onValueChange={setTipo}>
                <SelectTrigger className="h-9 w-full sm:w-48">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los tipos</SelectItem>
                  <SelectItem value="FE">Factura</SelectItem>
                  <SelectItem value="FNE">Factura Exenta</SelectItem>
                  <SelectItem value="FEX">Factura Extranjera</SelectItem>
                  <SelectItem value="DI">Declaración de Ingreso (DI 914)</SelectItem>
                  <SelectItem value="NC">Nota de Crédito</SelectItem>
                  <SelectItem value="NCEX">Nota de Crédito extranjera</SelectItem>
                </SelectContent>
              </Select>
              <Select value={pdfStatus} onValueChange={setPdfStatus}>
                <SelectTrigger className="h-9 w-full sm:w-40">
                  <SelectValue placeholder="PDF" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">PDF: Todos</SelectItem>
                  <SelectItem value="with">Con PDF</SelectItem>
                  <SelectItem value="without">Sin PDF</SelectItem>
                </SelectContent>
              </Select>
              <Select value={contab} onValueChange={setContab}>
                <SelectTrigger className="h-9 w-full sm:w-48">
                  <SelectValue placeholder="Contabilización" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Contab.: Todas</SelectItem>
                  <SelectItem value="sin_contabilizar">Sin contabilizar</SelectItem>
                  <SelectItem value="borrador">Borrador</SelectItem>
                  <SelectItem value="contabilizada">Contabilizada</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar proveedor / folio / RUT..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-9 w-full pl-8"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-4">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : invoices.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="Sin facturas cargadas"
              description="Cargue el export de BSale para registrar las facturas recibidas."
            />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="Sin resultados"
              description="No hay facturas con este filtro."
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableHeader
                      label="Proveedor"
                      sortKey="proveedor"
                      sort={sortConfig}
                      onSort={handleSort}
                    />
                    <TableHead>Empresa</TableHead>
                    <SortableHeader
                      label="Tipo"
                      sortKey="tipo"
                      sort={sortConfig}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="Folio"
                      sortKey="folio"
                      sort={sortConfig}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="Emisión"
                      sortKey="emision"
                      sort={sortConfig}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="Vencimiento"
                      sortKey="vencimiento"
                      sort={sortConfig}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="Estado"
                      sortKey="estado"
                      sort={sortConfig}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="Total"
                      sortKey="total"
                      sort={sortConfig}
                      onSort={handleSort}
                      align="right"
                      className="text-right"
                    />
                    <SortableHeader
                      label="Contabilización"
                      sortKey="contabilizacion"
                      sort={sortConfig}
                      onSort={handleSort}
                    />
                    <TableHead className="w-0 text-center">Documento</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pagination.pageItems.map((inv) => (
                    <TableRow key={inv.id}>
                      <TableCell className="font-medium">
                        {inv.supplier ? (
                          <Link
                            to={`/contabilidad/proveedores/${inv.supplier.id}`}
                            className="text-primary hover:underline"
                          >
                            {inv.supplier.name}
                          </Link>
                        ) : (
                          inv.razon_social
                        )}
                        <div className="text-xs text-muted-foreground">{inv.rut}</div>
                      </TableCell>
                      <TableCell>
                        {inv.posting_status === "sin_contabilizar" ? (
                          <Select
                            value={inv.company_id}
                            onValueChange={(v) =>
                              setInvoiceCompany.mutate({
                                invoiceId: inv.id,
                                companyId: v,
                              })
                            }
                          >
                            <SelectTrigger className="h-8 w-40 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {companies.map((c) => (
                                <SelectItem
                                  key={c.id}
                                  value={c.id}
                                  className="text-xs"
                                >
                                  {c.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-sm text-gray-700">
                            {inv.company?.name ?? "-"}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{TIPO_LABEL[inv.doc_tipo_code] ?? inv.doc_tipo_code}</TableCell>
                      <TableCell>{inv.folio}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(inv.emission_date)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {inv.due_date ? formatDate(inv.due_date) : "-"}
                        {inv.due_date_estimated && inv.due_date && (
                          <span className="ml-1 text-xs">(est.)</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="bg-gray-100 text-gray-700">
                          {inv.bsale_estado === "aceptado" ? "Aceptado" : "Recepcionado"}
                        </Badge>
                      </TableCell>
                      <TableCell
                        className={`text-right font-medium ${
                          Number(inv.total_clp) > 0 ? "text-green-700" : ""
                        }`}
                      >
                        ${formatCurrency(Number(inv.total_clp))}
                      </TableCell>
                      <TableCell>
                        {inv.posting_status === "sin_contabilizar" ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8"
                            onClick={() => setPostingInvoice(inv)}
                          >
                            Contabilizar
                          </Button>
                        ) : (
                          <Badge
                            variant="secondary"
                            className={POSTING_CHIP[inv.posting_status]?.className}
                          >
                            {POSTING_CHIP[inv.posting_status]?.label ??
                              inv.posting_status}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {inv.url_pdf ? (
                          // Authoritative BSale DTE.
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                aria-label="Ver factura"
                                onClick={() =>
                                  window.open(
                                    inv.url_pdf!,
                                    "_blank",
                                    "noopener,noreferrer"
                                  )
                                }
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Ver factura</TooltipContent>
                          </Tooltip>
                        ) : inv.manual_pdf_url ? (
                          // Manual attach (BSale never published the DTE).
                          <div className="inline-flex items-center justify-center gap-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  aria-label="Ver factura"
                                  onClick={() =>
                                    window.open(
                                      inv.manual_pdf_url!,
                                      "_blank",
                                      "noopener,noreferrer"
                                    )
                                  }
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                Ver factura (adjunto manual)
                              </TooltipContent>
                            </Tooltip>
                            <Badge
                              variant="secondary"
                              className="bg-blue-100 text-blue-800"
                            >
                              Adjunto manual
                            </Badge>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  aria-label="Acciones del adjunto"
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() => setAttachInvoice(inv)}
                                >
                                  <Paperclip className="mr-2 h-4 w-4" />
                                  Reemplazar adjunto
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="text-red-600 focus:text-red-600"
                                  onClick={() => setRemoveInvoice(inv)}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Quitar adjunto
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        ) : (
                          // No PDF from either source — offer to attach one.
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8"
                            onClick={() => setAttachInvoice(inv)}
                          >
                            <Paperclip className="mr-1.5 h-3.5 w-3.5" />
                            Adjuntar
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="border-t p-4">
                <TablePagination
                  page={pagination.page}
                  pageCount={pagination.pageCount}
                  totalItems={pagination.total}
                  pageSize={20}
                  onPageChange={pagination.setPage}
                  itemLabel="facturas"
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <SupplierInvoiceImportDialog open={importOpen} onOpenChange={setImportOpen} />
      <SupplierInvoiceManualDialog open={manualOpen} onOpenChange={setManualOpen} />
      <ContabilizarFacturaDialog
        invoice={postingInvoice}
        open={!!postingInvoice}
        onOpenChange={(o) => {
          if (!o) setPostingInvoice(null);
        }}
      />
      <AdjuntarPdfDialog
        invoice={attachInvoice}
        open={!!attachInvoice}
        onOpenChange={(o) => {
          if (!o) setAttachInvoice(null);
        }}
      />
      <AlertDialog
        open={!!removeInvoice}
        onOpenChange={(o) => {
          if (!o && !removeManualPdf.isPending) setRemoveInvoice(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Quitar adjunto</AlertDialogTitle>
            <AlertDialogDescription>
              Se quitará el PDF adjuntado manualmente de la factura
              {removeInvoice ? ` ${removeInvoice.folio}` : ""} de{" "}
              {removeInvoice?.razon_social ?? "este proveedor"}. No se elimina la
              factura ni su contabilización; luego podrás adjuntar otro
              documento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeManualPdf.isPending}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={removeManualPdf.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (!removeInvoice) return;
                removeManualPdf.mutate(removeInvoice.id, {
                  onSuccess: () => setRemoveInvoice(null),
                });
              }}
            >
              {removeManualPdf.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Quitar adjunto
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
